// Customer Statement DTOs (PD-13702 / PD-14041) — request + response contracts
// for the crm-api statements endpoints, shared with crm-web per CLAUDE.md so
// both sides type-check against the same shape.

import { Transform, Type } from 'class-transformer';
import {
	ArrayNotEmpty,
	IsArray,
	IsBoolean,
	IsInt,
	IsNumber,
	IsOptional,
	IsString,
	Max,
	Min,
	Validate,
} from 'class-validator';
import type { StatementRunEntity } from '../entity/statement-run.entity';
import type { StatementEntity } from '../entity/statement.entity';
import type { CustomerStatementRequestModel } from '../interfaces';
import { IsEmailAddressDtoOrString } from './send-email.dto';

export class GetCustomerStatementDto implements CustomerStatementRequestModel {
	@IsInt()
	public clientId!: number;

	@IsString()
	@IsOptional()
	public periodStart?: string | null;

	@IsString()
	@IsOptional()
	public periodEnd?: string | null;

	@IsString()
	@IsOptional()
	public asOfDate?: string | null;
}

export class EmailCustomerStatementDto extends GetCustomerStatementDto {
	@Validate(IsEmailAddressDtoOrString, [{ allowMultiple: true }])
	public to!: Array<string>;

	@IsArray()
	@IsOptional()
	public cc?: Array<string>;

	@IsArray()
	@IsOptional()
	public bcc?: Array<string>;

	@IsString()
	@IsOptional()
	public subject?: string;

	/** Optional intro paragraph rendered above the summary in the email body. */
	@IsString()
	@IsOptional()
	public message?: string;
}

// ---------------------------------------------------------------------------
// Stored statement records (PD-14041). POST /api/statements/generate takes the
// preview parameters plus, since PD-17144, whether to send the saved statement
// to the customer; the rest cover the list/read side of the stored records.
// ---------------------------------------------------------------------------

/** Body for POST /api/statements/generate. */
export class GenerateCustomerStatementDto extends GetCustomerStatementDto {
	/**
	 * PD-17144 — after saving, email the statement and notify the storefront
	 * users exactly as a statement run would, using the customer's statement
	 * recipient settings. Optional and false when absent so any caller that
	 * predates it keeps getting save-only; the CRM's New Statement modal sends
	 * it ticked by default.
	 */
	@IsBoolean()
	@IsOptional()
	public sendToCustomer?: boolean;
}

/**
 * PD-17144 — what sending a saved statement to the customer did, so the CRM
 * can say so. Sending never undoes the save: on a failure the statement stays
 * saved and `error` says why.
 */
export interface StatementDeliveryResultDto {
	/**
	 * Addresses the statement email was addressed to: the customer email on
	 * file, the other statement addresses and the storefront users, each once.
	 * Zero means the customer has no statement recipients set up.
	 */
	emailRecipients: number;
	/** Emails actually sent; fewer than emailRecipients when some failed. */
	emailsSent: number;
	/** Storefront users who got the "statement ready" notice. */
	storefrontUsersNotified: number;
	/** Of those, how many were also sent a text. */
	storefrontTextsSent: number;
	/** Why sending failed, when it did; null on success. */
	error: string | null;
}

/** Response of POST /api/statements/generate. */
export interface GenerateCustomerStatementResponseDto {
	success: boolean;
	data: CustomerStatementRecordDto;
	/** PD-17144 — null when the statement was only saved, not sent. */
	delivery: StatementDeliveryResultDto | null;
}

/** Query params for GET /api/statements (paginated stored-statement list). */
export class ListCustomerStatementsQueryDto {
	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@IsOptional()
	public page: number = 1;

	@Type(() => Number)
	@IsNumber()
	@Min(1)
	@Max(100)
	@IsOptional()
	public limit: number = 50;

	@Type(() => Number)
	@IsNumber()
	@IsOptional()
	public clientId?: number;

	/** Issuing company; validated against the caller's accessible companies. */
	@Type(() => Number)
	@IsNumber()
	@IsOptional()
	public companyId?: number;

	/** Inclusive lower bound on the statement as-of date (ISO YYYY-MM-DD). */
	@IsString()
	@IsOptional()
	public asOfDateStart?: string;

	/** Inclusive upper bound on the statement as-of date (ISO YYYY-MM-DD). */
	@IsString()
	@IsOptional()
	public asOfDateEnd?: string;

	/**
	 * PD-15467 — the list-builder list (or `saved:<id>`) the request comes
	 * from, so the server can apply that list's enforced filter rules (the
	 * salesperson filter locked to "Me" for callers without
	 * statements:view:any) exactly as the invoices list does.
	 */
	@IsString()
	@IsOptional()
	public listId?: string;

	/**
	 * PD-15467 — restrict to statements whose client has one of these users
	 * as the assigned OR alternate salesperson. `ME` is resolved server-side
	 * (the same token the invoice sales-rep filter uses).
	 */
	@Transform(({ value }: { value: unknown }) => {
		const values = Array.isArray(value) ? value : value != null ? [value] : [];
		return values.map((v) => (v === 'ME' ? 'ME' : Number(v)));
	})
	@IsArray()
	@IsOptional()
	public salesRepIds?: Array<number | 'ME'>;
}

/**
 * One row in the stored-statements list — everything a list screen needs
 * WITHOUT the (potentially large) JSON snapshot. Derived from the entity per
 * the shared-DTO rule; `statementNumber` (`ST-<id>`) and `clientName` are
 * computed/joined server-side rather than stored.
 */
export type CustomerStatementRecordListItemDto = Pick<
	StatementEntity,
	| 'id'
	| 'fkClientId'
	| 'fkCompanyId'
	| 'asOfDate'
	| 'periodStartDate'
	| 'periodEndDate'
	| 'endingBalance'
	| 'totalAmountDue'
	| 'generationSource'
	| 'distributedDate'
	| 'createdDate'
> & {
	/** Derived human reference (`ST-<id>`); not a stored column. */
	statementNumber: string;
	clientName: string;
	/**
	 * PD-17381 — the customer's number (`clientCustomerNumber`: external id,
	 * else internal id), the "Customer #" column. Joined from the client, not
	 * stored on the statement.
	 */
	customerNumber: string;
};

/** A full stored statement record — the list row plus the rendered snapshot. */
export type CustomerStatementRecordDto = CustomerStatementRecordListItemDto &
	Pick<StatementEntity, 'snapshotJson'>;

// ---------------------------------------------------------------------------
// Statement runs (PD-14044). POST /api/statements/runs queues one PENDING run
// per requested company; the BullMQ run processor picks pending runs up on its
// next tick and generates the statements.
// ---------------------------------------------------------------------------

/** Body for POST /api/statements/runs ("Add Statement Run"). */
export class CreateStatementRunDto {
	/** Issuing companies; validated against the caller's accessible companies. */
	@IsArray()
	@ArrayNotEmpty()
	@IsInt({ each: true })
	public companyIds!: Array<number>;

	@IsString()
	@IsOptional()
	public periodStart?: string | null;

	@IsString()
	@IsOptional()
	public periodEnd?: string | null;

	/** Defaults to today — the run's point-in-time stamp. */
	@IsString()
	@IsOptional()
	public asOfDate?: string | null;
}

/** One statement run row, as served by GET /api/statements/runs. */
export type StatementRunDto = Pick<
	StatementRunEntity,
	| 'id'
	| 'fkCompanyId'
	| 'fkClientId'
	| 'paymentTerm'
	| 'asOfDate'
	| 'periodStartDate'
	| 'periodEndDate'
	| 'status'
	| 'source'
	| 'statementsGenerated'
	| 'statementsSkipped'
	| 'statementsDistributed'
	| 'completedDate'
	| 'errorMessage'
	| 'resultMessage'
	| 'createdDate'
> & {
	companyName: string;
};

// ---------------------------------------------------------------------------
// Payment-term schedules (PD-16673). GET /api/statements/terms lists the terms
// customers are on for the Settings › Statements "Payment terms" table.
// ---------------------------------------------------------------------------

/** Customers on one payment term (customers only, the caller's companies). */
export interface StatementTermSummaryDto {
	/** The term as it reads — the spelling most of its customers have. */
	term: string;
	customerCount: number;
	/** Of those, the customers with Receive Statements on. */
	receivingCount: number;
	companies: Array<{
		companyId: number;
		customerCount: number;
		receivingCount: number;
	}>;
}

export interface StatementTermsResponseDto {
	/** Most customers first. */
	terms: Array<StatementTermSummaryDto>;
	/** Customers with no payment term, who always follow the company schedule. */
	customersWithoutTerm: number;
}
